/**
 * @namespace Notifications
 * @memberof Promega.Shell
 * @description Methods for showing and hiding dismissable notification flags near the main navigation.
 * If the current page URL includes a query string parameter <code>showmessage</code>, any message using that value for <code>data-message-name</code> will be shown automatically when this script is initialized.
 */

import closest from '../util/closest.js';
import getQueryStringParameter from '../util/getQueryStringParameter.js';
import removeQueryStringParameter from '../util/removeQueryStringParameter.js';
import isAnimationSupported from '../util/isAnimationSupported.js';
import trackEvent from '../util/trackEvent';

const DEFAULT_TIMEOUT = 5000;
const timeouts = [];

function handleCloseButtonClick(event) {
    const message = closest(event.target, ".notification-message");
    if (message) {
        hideMessage(message.getAttribute("data-message-name"));
        trackEvent("Components", "Notification - dismissed");
    }
}

function handleCloseAnimationEnd(event) {
    const message = event.target;
    message.setAttribute("hidden", "hidden");
    message.classList.remove("notification-message--hiding");
    message.removeEventListener("animationend", handleCloseAnimationEnd);
}

/**
 * @function showMessage
 * @memberof Promega.Shell.Notifications
 * @param {string} messageName A key identifying the message to be shown. Must match a DOM element with a `data-message-name` attribute set to the specified name.
 */
export function showMessage(messageName) {
    const message = document.querySelector(`.notification-message[data-message-name=${messageName}]`);
    if (message) {
        const closeButton = message.querySelector(".notification-message__close-button");
        if (closeButton) closeButton.addEventListener("click", handleCloseButtonClick);
        message.removeAttribute("hidden");

        timeouts.push({
            name: messageName,
            timeout: setTimeout(hideMessage.bind(null, messageName), DEFAULT_TIMEOUT)
        })
    }
}

/**
 * @function hideMessage
 * @memberof Promega.Shell.Notifications
 * @param {string} messageName A key identifying the message to be hidden. Must match a DOM element with a `data-message-name` attribute set to the specified name.
 */
export function hideMessage(messageName) {
    const message = document.querySelector(`.notification-message[data-message-name=${messageName}]`);
    if (message) {
        const closeButton = message.querySelector(".notification-message__close-button");
        if (closeButton) closeButton.removeEventListener("click", handleCloseButtonClick);

        if (isAnimationSupported()) {
            message.addEventListener("animationend", handleCloseAnimationEnd);
            message.classList.add("notification-message--hiding");
        }
        else message.setAttribute("hidden", "hidden");

        const timeoutIndex = timeouts.findIndex(current => current.name === messageName);
        if (timeoutIndex > -1) {
            clearTimeout(timeouts[timeoutIndex].timeout);
            timeouts.splice(timeoutIndex, 1);
        }
    }
}

if (typeof document !== "undefined") {
    const messageQueries = getQueryStringParameter(window.location.href.toLowerCase(), "showmessage")
        .split(",")
        .filter(current => current.length > 0);
    for (let i = 0; i < messageQueries.length; i++) {
        showMessage(messageQueries[i]);
    }

    if (messageQueries.length > 0 && window.history) {
        history.replaceState({}, "", removeQueryStringParameter(location.href.toLowerCase(), "showmessage"));
    }
}
